import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine, Cell,
} from "recharts";
import { Panel, PanelHeader, Kpi } from "@/components/common/Card";
import { DirectionPill, ThesisInvalidationBadge } from "@/components/common/Pill";
import {
  getPaperAccount, getPaperTrades, getPaperEquity, getPaperAnalytics,
  backfillPaper, resetPaperAccount, formatUsd, formatSignedUsd,
  formatNum, formatSignedPct, timeAgo,
} from "@/lib/api";
import { PAPER } from "@/constants/testIds";
import {
  Wallet, ChartLineUp, ArrowsClockwise, TrendUp, TrendDown,
  Database, ChartBar,
} from "@phosphor-icons/react";

const STRAT_LABEL = {
  trend_pullback: "Trend Pullback", breakout_retest: "Breakout Retest",
  range_trading: "Range Trading", liquidity_sweep: "Liquidity Sweep",
};
const DEFAULT_CUTOFF = "2026-05-31T22:59:31Z";

// datetime-local inputs use "YYYY-MM-DDTHH:mm:ss" in the browser's local time,
// with no timezone suffix — convert both ways so the value we send the API
// stays a proper UTC ISO string regardless of the viewer's timezone.
const isoToLocalInput = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const localInputToIso = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

const ColorUsd = ({ value }) => (
  <span className={Number(value) >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"}>
    {formatSignedUsd(value)}
  </span>
);

const AnalyticsTable = ({ rows, testid, labelMap }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70">
          <th className="text-left font-bold px-4 py-2">Group</th>
          <th className="text-right font-bold px-4 py-2">Trades</th>
          <th className="text-right font-bold px-4 py-2">Win %</th>
          <th className="text-right font-bold px-4 py-2">PF</th>
          <th className="text-right font-bold px-4 py-2">Cum R</th>
          <th className="text-right font-bold px-4 py-2">Net PnL</th>
        </tr>
      </thead>
      <tbody>
        {(rows || []).length === 0 ? (
          <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-slate-400">No closed trades yet.</td></tr>
        ) : rows.map((r) => (
          <tr key={r.key} className="border-b border-slate-200/70">
            <td className="px-4 py-2.5 text-slate-800 text-xs">{(labelMap && labelMap[r.key]) || r.key}</td>
            <td className="px-4 py-2.5 text-right font-data text-slate-700">{r.trades}</td>
            <td className="px-4 py-2.5 text-right font-data text-slate-700">{r.win_rate === null ? "—" : `${formatNum(r.win_rate, 1)}%`}</td>
            <td className="px-4 py-2.5 text-right font-data text-slate-700">{r.profit_factor === null ? "—" : formatNum(r.profit_factor, 2)}</td>
            <td className="px-4 py-2.5 text-right font-data text-slate-700">{formatNum(r.cumulative_r, 2)}</td>
            <td className="px-4 py-2.5 text-right font-data"><ColorUsd value={r.net_pnl} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TABS = [
  { key: "by_strategy", label: "Strategy", testid: PAPER.byStrategy, map: STRAT_LABEL },
  { key: "by_regime", label: "Regime", testid: PAPER.byRegime },
  { key: "by_direction", label: "Direction", testid: PAPER.byDirection },
  { key: "by_score_bucket", label: "Score", testid: PAPER.byScore },
  { key: "by_pair", label: "Pair", testid: PAPER.byPair },
];

export const PaperTrading = () => {
  const { data: acct, mutate: mA } = useSWR("/paper/account", getPaperAccount, { refreshInterval: 15000 });
  const { data: eq, mutate: mE } = useSWR("/paper/equity", getPaperEquity, { refreshInterval: 20000 });
  const { data: an, mutate: mAn } = useSWR("/paper/analytics", getPaperAnalytics, { refreshInterval: 20000 });
  const { data: open, mutate: mO } = useSWR(["/paper/trades", "OPEN"], () => getPaperTrades({ status: "OPEN", limit: 100 }), { refreshInterval: 15000 });
  const { data: closed, mutate: mC } = useSWR(["/paper/trades", "CLOSED"], () => getPaperTrades({ status: "CLOSED", limit: 100 }), { refreshInterval: 20000 });

  const [tab, setTab] = useState("by_strategy");
  const [cutoff, setCutoff] = useState(DEFAULT_CUTOFF);
  const [busy, setBusy] = useState("");

  const refreshAll = () => Promise.all([mA(), mE(), mAn(), mO(), mC()]);

  const onBackfill = async () => {
    setBusy("backfill");
    try {
      const r = await backfillPaper(cutoff);
      await refreshAll();
      toast.success("Backfill complete", {
        description: `${r.closed_settled} closed settled · ${r.open_positions} open · balance ${formatUsd(r.account?.balance)}`,
      });
    } catch (e) {
      toast.error("Backfill failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(""); }
  };
  const onReset = async () => {
    if (!window.confirm("Reset the paper portfolio? All virtual trades will be cleared.")) return;
    setBusy("reset");
    try {
      await resetPaperAccount();
      await refreshAll();
      toast.success("Paper portfolio reset");
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(""); }
  };

  const startBal = acct?.starting_balance ?? 10000;
  const equity = (eq?.equity_curve || []).map((p, i) => ({ i, balance: p.balance }));
  const dd = (eq?.drawdown_curve || []).map((p, i) => ({ i, drawdown: -Math.abs(p.drawdown_pct) }));
  const daily = eq?.daily_pnl || [];

  return (
    <div data-testid={PAPER.shell} className="flex flex-col gap-6 max-w-[1600px]">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">Virtual Portfolio · V1</div>
          <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-3">
            <Wallet size={30} weight="bold" className="text-[#2563eb]" /> Paper Trading
          </h1>
          <div className="text-xs text-slate-500 mt-2">
            Risk-based sizing ({formatNum(acct?.starting_balance ? undefined : 1)}1% risk/trade) · no leverage on P&L · fees applied per side · simulation only.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="datetime-local"
            step="1"
            data-testid="paper-cutoff-input"
            value={isoToLocalInput(cutoff)}
            onChange={(e) => {
              const iso = localInputToIso(e.target.value);
              if (iso) setCutoff(iso);
            }}
            className="text-xs font-data border border-slate-200 rounded-sm px-2 py-2 w-[220px] bg-white"
          />
          <button data-testid={PAPER.backfillBtn} onClick={onBackfill} disabled={!!busy}
            className="inline-flex items-center gap-2 border border-[#2563eb]/30 hover:border-[#2563eb] text-[#2563eb] text-[10px] font-bold tracking-widest uppercase px-3 py-2 rounded-sm transition disabled:opacity-50">
            <Database size={13} weight="bold" />{busy === "backfill" ? "Backfilling…" : "Backfill"}
          </button>
          <button data-testid={PAPER.resetBtn} onClick={onReset} disabled={!!busy}
            className="inline-flex items-center gap-2 border border-[#dc2626]/30 hover:border-[#dc2626] text-[#dc2626] text-[10px] font-bold tracking-widest uppercase px-3 py-2 rounded-sm transition disabled:opacity-50">
            <ArrowsClockwise size={13} weight="bold" />{busy === "reset" ? "Resetting…" : "Reset"}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Kpi testid={PAPER.kpiBalance} label="Balance" value={formatUsd(acct?.balance)} sub={`Start ${formatUsd(startBal, 0)}`} />
        <Kpi testid={PAPER.kpiEquity} label="Equity" value={formatUsd(acct?.equity)} sub={`Unrl ${formatSignedUsd(acct?.unrealized_pnl)}`} />
        <Kpi testid={PAPER.kpiNetProfit} label="Net Profit" value={<ColorUsd value={acct?.net_profit ?? 0} />} sub={`${formatSignedPct(acct?.return_percent)} return`} />
        <Kpi testid={PAPER.kpiWinRate} label="Win Rate" value={acct?.win_rate == null ? "—" : `${formatNum(acct.win_rate, 1)}%`} sub={`${acct?.wins ?? 0}W · ${acct?.losses ?? 0}L`} />
        <Kpi testid={PAPER.kpiProfitFactor} label="Profit Factor" value={acct?.profit_factor == null ? "—" : formatNum(acct.profit_factor, 2)} sub="gross win / loss" />
        <Kpi testid={PAPER.kpiAvgR} label="Avg R" value={acct?.avg_r == null ? "—" : formatNum(acct.avg_r, 2)} sub={`Cum R ${formatNum(acct?.cumulative_r, 2)}`} />
        <Kpi testid={PAPER.kpiMaxDd} label="Max Drawdown" value={<span className="text-[#dc2626]">{formatUsd(acct?.max_drawdown_usd)}</span>} sub={`${formatNum(acct?.max_drawdown_pct, 2)}%`} />
        <Kpi label="Fees Paid" value={formatUsd(acct?.fees_paid)} sub="cumulative" />
        <Kpi label="Open" value={acct?.open_trades ?? 0} sub="positions" />
        <Kpi label="Closed" value={acct?.closed_trades ?? 0} sub="settled" />
      </div>

      {/* Equity + Drawdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel data-testid={PAPER.equityChart}>
          <PanelHeader title="Equity Curve" hint="Balance after each closed trade" action={<ChartLineUp size={14} className="text-slate-500" />} />
          <div className="p-4 h-[240px]">
            {equity.length <= 1 ? <Empty text="No closed trades yet." /> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={equity} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs><linearGradient id="eqf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563eb" stopOpacity={0.25} /><stop offset="100%" stopColor="#2563eb" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="i" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} domain={["auto", "auto"]} width={58} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                  <Tooltip formatter={(v) => [formatUsd(v), "Balance"]} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                  <ReferenceLine y={startBal} stroke="#94a3b8" strokeDasharray="4 4" />
                  <Area type="monotone" dataKey="balance" stroke="#2563eb" strokeWidth={2} fill="url(#eqf)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Panel>
        <Panel data-testid={PAPER.drawdownChart}>
          <PanelHeader title="Drawdown Curve" hint="Peak-to-trough % over time" action={<TrendDown size={14} className="text-slate-500" />} />
          <div className="p-4 h-[240px]">
            {dd.length <= 1 ? <Empty text="No closed trades yet." /> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dd} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs><linearGradient id="ddf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#dc2626" stopOpacity={0} /><stop offset="100%" stopColor="#dc2626" stopOpacity={0.3} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="i" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} width={44} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => [`${formatNum(Math.abs(v), 2)}%`, "Drawdown"]} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                  <Area type="monotone" dataKey="drawdown" stroke="#dc2626" strokeWidth={2} fill="url(#ddf)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Panel>
      </div>

      {/* Daily PnL */}
      <Panel data-testid={PAPER.dailyPnlChart}>
        <PanelHeader title="Daily P&L" hint="Realized USD per day" action={<ChartBar size={14} className="text-slate-500" />} />
        <div className="p-4 h-[220px]">
          {daily.length === 0 ? <Empty text="No closed trades yet." /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} width={54} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => [formatSignedUsd(v), "P&L"]} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Bar dataKey="pnl">
                  {daily.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? "#16a34a" : "#dc2626"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Panel>

      {/* Analytics tabs */}
      <Panel>
        <PanelHeader title="P&L Analytics" hint={`${an?.closed_trades ?? 0} closed trades`}
          action={
            <div className="flex gap-1">
              {TABS.map((t) => (
                <button key={t.key} data-testid={`paper-tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-sm transition ${tab === t.key ? "bg-[#2563eb] text-white" : "text-slate-500 hover:bg-[#e2e8f0]"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          } />
        <AnalyticsTable
          rows={an?.[tab]}
          testid={TABS.find((t) => t.key === tab)?.testid}
          labelMap={TABS.find((t) => t.key === tab)?.map}
        />
      </Panel>

      {/* Open positions */}
      <TradeTable title="Open Positions" testid={PAPER.openTrades} rows={open} kind="open" />
      {/* Closed trades */}
      <TradeTable title="Closed Trades" testid={PAPER.closedTrades} rows={closed} kind="closed" />
    </div>
  );
};

const Empty = ({ text }) => (
  <div className="h-full flex items-center justify-center text-xs text-slate-400">{text}</div>
);

const TradeTable = ({ title, testid, rows, kind }) => (
  <Panel>
    <PanelHeader title={title} hint={`${rows?.length || 0} ${kind === "open" ? "live" : "settled"}`}
      action={kind === "open" ? <TrendUp size={14} className="text-slate-500" /> : <Wallet size={14} className="text-slate-500" />} />
    <div className="overflow-x-auto">
      <table data-testid={testid} className="w-full text-sm">
        <thead>
          <tr className="text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70">
            <th className="text-left font-bold px-4 py-2">Pair</th>
            <th className="text-left font-bold px-4 py-2">Dir</th>
            <th className="text-right font-bold px-4 py-2">Entry</th>
            <th className="text-right font-bold px-4 py-2">Size $</th>
            <th className="text-right font-bold px-4 py-2">Risk $</th>
            {kind === "open" && <th className="text-right font-bold px-4 py-2">PnL $</th>}
            {kind === "closed" && <th className="text-right font-bold px-4 py-2">R</th>}
            {kind === "closed" && <th className="text-right font-bold px-4 py-2">Fees</th>}
            {kind === "closed" && <th className="text-right font-bold px-4 py-2">PnL $</th>}
            {kind === "closed" && <th className="text-right font-bold px-4 py-2">Balance</th>}
            <th className="text-left font-bold px-4 py-2">Strategy</th>
            <th className="text-right font-bold px-4 py-2">{kind === "open" ? "Opened" : "Closed"}</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).length === 0 ? (
            <tr><td colSpan={kind === "closed" ? 11 : 8} className="px-4 py-10 text-center text-xs text-slate-400">No {kind} paper trades.</td></tr>
          ) : rows.map((t) => (
            <tr key={t.id} data-testid={`paper-${kind}-row-${t.id}`} className="border-b border-slate-200/70 hover:bg-[#e2e8f0]">
              <td className="px-4 py-2.5 font-data text-slate-900">
                <div className="flex items-center gap-1.5">
                  {t.pair}
                  {kind === "open" && <ThesisInvalidationBadge signal={t} />}
                </div>
              </td>
              <td className="px-4 py-2.5"><DirectionPill direction={t.direction} /></td>
              <td className="px-4 py-2.5 text-right font-data text-slate-700">{formatNum(t.entry)}</td>
              <td className="px-4 py-2.5 text-right font-data text-slate-700">{formatUsd(t.position_size_usd)}</td>
              <td className="px-4 py-2.5 text-right font-data text-slate-500">{formatUsd(t.risk_amount)}</td>
              {kind === "open" && <td className="px-4 py-2.5 text-right font-data">{t.unrealized_pnl_usd == null ? "—" : <ColorUsd value={t.unrealized_pnl_usd} />}</td>}
              {kind === "closed" && <td className="px-4 py-2.5 text-right font-data"><span className={Number(t.realized_r) >= 0 ? "text-[#2563eb]" : "text-[#dc2626]"}>{formatNum(t.realized_r, 2)}R</span></td>}
              {kind === "closed" && <td className="px-4 py-2.5 text-right font-data text-slate-400">{formatUsd(t.fees_usd)}</td>}
              {kind === "closed" && <td className="px-4 py-2.5 text-right font-data"><ColorUsd value={t.realized_pnl_usd} /></td>}
              {kind === "closed" && <td className="px-4 py-2.5 text-right font-data text-slate-700">{formatUsd(t.balance_after)}</td>}
              <td className="px-4 py-2.5 text-slate-600 text-xs">{STRAT_LABEL[t.strategy] || t.strategy}</td>
              <td className="px-4 py-2.5 text-right text-slate-500 text-xs">{timeAgo(kind === "open" ? t.opened_at : t.closed_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Panel>
);

export default PaperTrading;
